/**
 * ScoreChip — Phase 8 T-01 §2E.
 * Filled emerald pill for match % scores; graded by tier.
 *
 * Tiers (spec §2E + §19 quick-ref):
 *   ≥90 → emerald-500 (#10B981) white text
 *   70–89 → emerald-400 (#34D399) dark text
 *   50–69 → emerald-300 (#6EE7B7) dark text
 *   30–49 → amber-400   (#FBBF24) dark text
 *   <30  → zinc-300     (#D4D4D8) dark text
 *
 * Size: h-5 px-2 text-[11px] font-medium rounded-full (spec §2E).
 */

type ScoreChipProps = {
  /** Score as a 0–100 integer or float (displayed rounded to nearest %). */
  score: number;
  /** If true, shows a "✓" prefix on the highest tier (≥90). */
  showCheck?: boolean;
};

type Tier = {
  bg: string;
  text: string;
};

function getTier(pct: number): Tier {
  if (pct >= 90) return { bg: '#10B981', text: '#ffffff' };
  if (pct >= 70) return { bg: '#34D399', text: '#065F46' };
  if (pct >= 50) return { bg: '#6EE7B7', text: '#065F46' };
  if (pct >= 30) return { bg: '#FBBF24', text: '#78350F' };
  return          { bg: '#D4D4D8', text: '#3F3F46' };
}

export function ScoreChip({ score, showCheck = false }: ScoreChipProps) {
  // Tolerate legacy persisted rows that store `composite_score` on a
  // 0-1 scale. Anything ≤ 1 is treated as a fraction and rescaled so
  // old KPI data doesn't render as "0%" or "1%".
  const raw = Number.isFinite(score) ? score : 0;
  const pct = Math.round(raw > 1 ? raw : raw * 100);
  const safePct = Math.max(0, Math.min(100, pct));
  const { bg, text } = getTier(safePct);
  const label = `${safePct}%`;
  const prefix = showCheck && safePct >= 90 ? '✓ ' : '';

  return (
    <span
      className="inline-flex items-center h-5 px-2 text-[11px] font-medium rounded-full whitespace-nowrap"
      style={{ backgroundColor: bg, color: text }}
      aria-label={`Score ${safePct}%`}
    >
      {prefix}{label}
    </span>
  );
}
